// Demande de réservation depuis le portail (M19) : équipement ouvert en ligne de la collectivité choisie,
// semaine, créneau libre (une réservation acceptée ou confirmée occupe le créneau), plage horaire dans le
// créneau, objet ; un équipement payant est payé après acceptation par la mairie. Heures de Libreville.
import { formaterFrancs, lundiDeLaSemaine } from '@cc/contrats';
import { Bandeau, Bouton, Champ, EtatVide, LienBouton, Selection } from '@cc/ui';
import { CalendarRange } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { useCollectiviteChoisie } from '../collectivite';
import { Bloc, EnTeteCitoyen, ExigeConnexion } from '../commun';
import { CLES } from '../api';
import { CLES_RESERVATIONS, demanderReservation, useEquipementsPortail, type ReservationUsager } from './api';
import styles from '../citoyen.module.css';
import depot from '../depot/depot.module.css';

const aujourdhui = () => new Date(Date.now() + 3_600_000).toISOString().slice(0, 10);
const decaler = (lundi: string, n: number) => new Date(Date.parse(`${lundi}T12:00:00Z`) + n * 7 * 86_400_000).toISOString().slice(0, 10);
const jourLong = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

function Demande({ code }: { code: string }) {
  const [semaine, setSemaine] = useState(() => lundiDeLaSemaine(aujourdhui()));
  const equipements = useEquipementsPortail(code, semaine);
  const [equipementId, setEquipementId] = useState('');
  const [choix, setChoix] = useState<{ date: string; heure_debut: string; heure_fin: string } | null>(null);
  const [plage, setPlage] = useState({ heure_debut: '', heure_fin: '' });
  const [objet, setObjet] = useState('');
  const [participants, setParticipants] = useState('');
  const [faite, setFaite] = useState<ReservationUsager | null>(null);
  const action = useAction({
    executer: () => demanderReservation({ collectivite: code, equipement_id: equipementId, date: choix?.date, heure_debut: plage.heure_debut, heure_fin: plage.heure_fin, objet: objet.trim(), participants: participants ? Number(participants) : undefined }),
    invalider: [[...CLES_RESERVATIONS.mes], ['citoyen', 'equipements'], [...CLES.notifications]],
    apres: (r) => setFaite(r),
  });
  const eq = (equipements.data?.elements ?? []).find((e) => e.id === equipementId);
  if (faite) {
    return (
      <div className={styles.pile}>
        <Bloc etiquette={`Demande ${faite.numero} envoyée`} titre={`${faite.equipement.nom}, ${date(faite.date)} de ${faite.heure_debut} à ${faite.heure_fin}`}>
          <p className={styles.blocTexte}>La mairie examine votre demande. {faite.montant_estime !== null ? `Montant estimé : ${formaterFrancs(faite.montant_estime)}${faite.fictif ? ' (fictif)' : ''}, à payer après acceptation (${faite.regle ?? 'règle du tarif'}).` : 'Réservation gratuite.'}</p>
        </Bloc>
        <LienBouton href="/citoyen/reservations" variante="principal">Mes réservations</LienBouton>
      </div>
    );
  }
  return (
    <div className={styles.pile}>
      <EtatRequete chargement={equipements.isPending} erreur={equipements.error} onReessayer={() => void equipements.refetch()} fonction="Les équipements réservables" lignes={3}>
        {equipements.data && !equipements.data.module_actif ? (
          <EtatVide titre="Réservations non ouvertes" icone={CalendarRange}><p>Cette collectivité n&apos;a pas activé les réservations d&apos;équipements en ligne.</p></EtatVide>
        ) : (equipements.data?.elements ?? []).length === 0 ? (
          <EtatVide titre="Aucun équipement réservable en ligne" icone={CalendarRange}><p>Adressez-vous à la mairie pour réserver une salle ou un terrain.</p></EtatVide>
        ) : (
          <Selection id="res-equipement" libelle="Équipement" optionVide="Choisir un équipement" options={(equipements.data?.elements ?? []).map((e) => ({ valeur: e.id, libelle: `${e.nom}${e.payant ? ' (payant)' : ' (gratuit)'}` }))} value={equipementId}
            onChange={(e) => { setEquipementId(e.target.value); setChoix(null); }} aide={eq ? [eq.description, eq.localisation, eq.capacite ? `${eq.capacite} personnes` : null].filter(Boolean).join(' · ') : undefined} />
        )}
      </EtatRequete>
      {eq ? (
        <>
          <div className={styles.ligneBoutons} role="group" aria-label="Semaine">
            <Bouton variante="secondaire" petit disabled={semaine <= lundiDeLaSemaine(aujourdhui())} onClick={() => { setSemaine(decaler(semaine, -1)); setChoix(null); }}>Semaine précédente</Bouton>
            <span>Semaine du {jourLong(semaine)}</span>
            <Bouton variante="secondaire" petit onClick={() => { setSemaine(decaler(semaine, 1)); setChoix(null); }}>Semaine suivante</Bouton>
          </div>
          {eq.jours.filter((j) => j.creneaux.length > 0 && j.date >= aujourdhui()).length === 0 ? <EtatVide titre="Aucun créneau cette semaine" icone={CalendarRange}><p>Essayez la semaine suivante.</p></EtatVide> : null}
          {eq.jours.filter((j) => j.creneaux.length > 0 && j.date >= aujourdhui()).map((j) => (
            <fieldset key={j.date} className={depot.groupe}>
              <legend>{jourLong(j.date)}</legend>
              <div className={styles.filtres} role="radiogroup" aria-label={`Créneaux du ${jourLong(j.date)}`}>
                {j.creneaux.map((c) => (
                  <button key={c.heure_debut} type="button" role="radio" className={styles.filtre} disabled={c.occupe} aria-checked={choix?.date === j.date && choix.heure_debut === c.heure_debut}
                    aria-label={`${c.heure_debut} à ${c.heure_fin}, ${c.occupe ? 'déjà réservé' : 'libre'}`}
                    onClick={() => { setChoix({ date: j.date, heure_debut: c.heure_debut, heure_fin: c.heure_fin }); setPlage({ heure_debut: c.heure_debut, heure_fin: c.heure_fin }); }}>
                    <span className={styles.mono}>{c.heure_debut} à {c.heure_fin}</span>{c.occupe ? ' réservé' : ''}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
        </>
      ) : null}
      {choix ? (
        <>
          <p role="status">Créneau choisi : <strong>{jourLong(choix.date)}, {choix.heure_debut} à {choix.heure_fin}</strong></p>
          <div className={styles.ligneBoutons}>
            <Champ id="res-debut" libelle="Arrivée" type="time" min={choix.heure_debut} max={choix.heure_fin} value={plage.heure_debut} onChange={(e) => setPlage((p) => ({ ...p, heure_debut: e.target.value }))} />
            <Champ id="res-fin" libelle="Départ" type="time" min={choix.heure_debut} max={choix.heure_fin} value={plage.heure_fin} onChange={(e) => setPlage((p) => ({ ...p, heure_fin: e.target.value }))} />
          </div>
          <Champ id="res-objet" libelle="Objet de la réservation" value={objet} onChange={(e) => setObjet(e.target.value)} required />
          <Champ id="res-participants" libelle="Nombre de participants" type="number" min={1} value={participants} onChange={(e) => setParticipants(e.target.value)} facultatif />
          {eq?.payant ? <p className={styles.aide}>Équipement payant : le montant vous est indiqué avec la demande ; vous payez après acceptation par la mairie, puis la réservation est confirmée.</p> : null}
          {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
          <Bouton pleineLargeur chargement={action.enCours} libelleChargement="Envoi de la demande" disabled={objet.trim().length < 3} onClick={() => action.lancer(undefined)}>Envoyer la demande</Bouton>
        </>
      ) : null}
    </div>
  );
}

export function PageNouvelleReservation() {
  const [choisie] = useCollectiviteChoisie();
  if (!choisie) return <Navigate to="/citoyen" replace />;
  return (
    <>
      <EnTeteCitoyen titre="Réserver un équipement" sousTitre={`Salles et terrains de ${choisie.nom}.`} retour="/citoyen/reservations" libelleRetour="Réservations" />
      <ExigeConnexion titre="Connectez-vous pour réserver">
        <Demande code={choisie.code} />
      </ExigeConnexion>
    </>
  );
}
